<template>
  <div class="home">
    <!-- ===== HERO ===== -->
    <section class="hero" ref="hero">
      <div class="hero-bg"></div>
      <div class="hero-grid"></div>
      <div class="hero-scan"></div>

      <div class="hero-content" :class="{ visible: heroVisible }">
        <p class="hero-eyebrow">Alexandria's #1 Gym</p>
        <h1 class="hero-title">
          <span class="title-venom">VENOM</span>
          <span class="title-gym">GYM</span>
        </h1>
        <p class="hero-sub">Train Hard. Stay Strong. Forge Your Legend.</p>
        <div class="hero-btns">
          <router-link to="/contact" class="btn-primary">Join Now</router-link>
          <router-link to="/about" class="btn-ghost">Our Story</router-link>
        </div>
      </div>

      <div class="hero-stats">
        <div class="stat" v-for="s in stats" :key="s.label">
          <span class="stat-num">{{ s.value }}</span>
          <span class="stat-label">{{ s.label }}</span>
        </div>
      </div>

      <div class="scroll-hint">
        <span></span>
        <p>Scroll</p>
      </div>
    </section>

    <!-- ===== GALLERY SLIDER ===== -->
    <section class="gallery-section">
      <div class="section-header">
        <span class="section-tag">Inside VENOM</span>
        <h2>Our Gym Halls</h2>
      </div>
      <div class="slider-wrap">
        <div class="slide-track">
          <div
            class="slide"
            v-for="(img, i) in [...gallery, ...gallery]"
            :key="i"
          >
            <img :src="img" loading="lazy" />
            <div class="slide-overlay"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== STATS BANNER ===== -->
    <section class="banner">
      <div class="banner-inner">
        <div class="banner-item" v-for="b in bannerItems" :key="b.label">
          <span class="banner-icon" v-html="b.icon"></span>
          <div>
            <p class="banner-val">{{ b.value }}</p>
            <p class="banner-lbl">{{ b.label }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== PRICING ===== -->
    <section class="pricing" ref="pricing">
      <div class="section-header">
        <span class="section-tag">Flexible Plans</span>
        <h2>Membership Plans</h2>
        <p class="section-sub">
          Choose the plan that fits your goals and budget
        </p>
      </div>

      <div class="plans-grid">
        <div
          class="plan-card"
          v-for="(plan, i) in plans"
          :key="i"
          :class="{ featured: plan.featured }"
          :style="{ '--delay': i * 0.1 + 's' }"
        >
          <div class="plan-badge" v-if="plan.featured">Most Popular</div>
          <div class="plan-top">
            <h3 class="plan-name">{{ plan.name }}</h3>
            <div class="plan-price">
              <span class="plan-amount">{{ plan.price }}</span>
            </div>
          </div>
          <ul class="plan-features">
            <li v-for="(f, j) in plan.features" :key="j">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {{ f }}
            </li>
          </ul>
          <router-link to="/contact" class="plan-btn">Get Started</router-link>
        </div>
      </div>
    </section>

    <!-- ===== SERVICES ===== -->
    <section class="services">
      <div class="section-header">
        <span class="section-tag">What We Offer</span>
        <h2>Sports & Activities</h2>
      </div>
      <div class="services-grid">
        <div class="service-card" v-for="(s, i) in services" :key="i">
          <div class="service-img-wrap">
            <img :src="s.img" :alt="s.name" loading="lazy" />
            <div class="service-overlay"></div>
          </div>
          <div class="service-info">
            <h4>{{ s.name }}</h4>
            <span class="service-arrow">→</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== CTA BANNER ===== -->
    <section class="cta-section">
      <div class="cta-bg"></div>
      <div class="cta-content">
        <h2>Ready to Transform?</h2>
        <p>
          Join hundreds of members who already chose VENOM GYM as their home.
        </p>
        <router-link to="/contact" class="btn-primary large"
          >Start Today</router-link
        >
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: "HomeView",
  data() {
    return {
      heroVisible: false,
      stats: [
        { value: "500+", label: "Members" },
        { value: "3+", label: "Expert Trainers" },
        { value: "5+", label: "Programs" },
        { value: "7", label: "Days / Week" },
      ],
      bannerItems: [
        { icon: "🏋️", value: "Pro Equipment", label: "World-Class Machines" },
        { icon: "👊", value: "Boxing & MMA", label: "Combat Sports" },
        { icon: "🥗", value: "Nutrition Plans", label: "Diet & Coaching" },
        { icon: "⏰", value: "6AM – 12AM", label: "Open 7 Days" },
      ],
      gallery: [
        "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1583454110551-21f2fa2afe61?auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1558611848-73f7eb4001a1?auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1579758629938-03607ccdbaba?auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1605296867304-46d5465a13f1?auto=format&fit=crop&w=800&q=80",
        "https://images.unsplash.com/photo-1517960413843-0aee8e2b3285?auto=format&fit=crop&w=800&q=80",
      ],
      plans: [
        {
          name: "Monthly",
          price: "450 EGP",
          features: ["Full Gym Access", "Free Trainer Advice", "Locker Room"],
        },
        {
          name: "3 Months",
          price: "1,200 EGP",
          features: [
            "Gym + Cardio Zone",
            "Custom Diet Plan",
            "Progress Tracking",
          ],
          featured: true,
        },
        {
          name: "2 Person",
          price: "850 EGP",
          features: ["All Programs", "Personal Coach", "Flexible Schedule"],
        },
        {
          name: "3 Person",
          price: "1,250 EGP",
          features: ["Unlimited Access", "Group Coaching", "Priority Booking"],
        },
        {
          name: "VIP",
          price: "800 EGP",
          features: ["Private Coach", "Luxury Facilities", "Priority Service"],
        },
      ],
      services: [
        {
          name: "Boxing",
          img: "https://images.unsplash.com/photo-1549719386-74dfcbf7dbed?auto=format&fit=crop&w=600&q=80",
        },
        {
          name: "Kick Boxing",
          img: "https://images.unsplash.com/photo-1555597673-b21d5c935865?auto=format&fit=crop&w=600&q=80",
        },
        {
          name: "MMA",
          img: "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?auto=format&fit=crop&w=600&q=80",
        },
        {
          name: "Karate",
          img: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?auto=format&fit=crop&w=600&q=80",
        },
        {
          name: "Fitness",
          img: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=600&q=80",
        },
      ],
    };
  },
  mounted() {
    setTimeout(() => {
      this.heroVisible = true;
    }, 100);
    this.setupScrollReveal();
  },
  methods: {
    setupScrollReveal() {
      const observer = new IntersectionObserver(
        (entries) =>
          entries.forEach((e) => {
            if (e.isIntersecting) e.target.classList.add("revealed");
          }),
        { threshold: 0.12 }
      );
      document
        .querySelectorAll(".plan-card, .service-card, .banner-item")
        .forEach((el) => observer.observe(el));
    },
  },
};
</script>

<style scoped>
/* ==============================
   GLOBAL
============================== */
.home {
  background: var(--black);
  color: var(--white);
  overflow-x: hidden;
}

.section-header {
  text-align: center;
  margin-bottom: 56px;
}
.section-tag {
  display: inline-block;
  font-family: "Rajdhani", sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: var(--red);
  margin-bottom: 10px;
  padding: 4px 14px;
  border: 1px solid rgba(204, 0, 0, 0.3);
  border-radius: 2px;
}
.section-header h2 {
  font-family: "Bebas Neue", cursive;
  font-size: clamp(2.2rem, 5vw, 3.5rem);
  letter-spacing: 3px;
  color: var(--white);
  line-height: 1;
}
.section-sub {
  color: var(--grey);
  font-size: 0.95rem;
  margin-top: 12px;
}

.btn-primary {
  display: inline-block;
  background: var(--red);
  color: #fff;
  padding: 14px 32px;
  font-family: "Bebas Neue", cursive;
  font-size: 1.1rem;
  letter-spacing: 2px;
  text-decoration: none;
  clip-path: polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%);
  transition: all 0.3s ease;
}
.btn-primary:hover {
  background: var(--red-glow);
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(204, 0, 0, 0.5);
}
.btn-primary.large {
  padding: 18px 48px;
  font-size: 1.3rem;
}

.btn-ghost {
  display: inline-block;
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  padding: 13px 30px;
  font-family: "Bebas Neue", cursive;
  font-size: 1.1rem;
  letter-spacing: 2px;
  text-decoration: none;
  clip-path: polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%);
  transition: all 0.3s ease;
}
.btn-ghost:hover {
  border-color: var(--red);
  color: var(--red);
  transform: translateY(-3px);
}

/* ==============================
   HERO
============================== */
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
}
.hero-bg {
  position: absolute;
  inset: 0;
  background: url("@/assets/backgrund.png") center / cover no-repeat;
  transform: scale(1.05);
  animation: heroZoom 18s ease-in-out infinite alternate;
  filter: brightness(0.22) saturate(0.6);
}
@keyframes heroZoom {
  from {
    transform: scale(1.05);
  }
  to {
    transform: scale(1.12);
  }
}

.hero-bg::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    120deg,
    rgba(120, 0, 0, 0.35) 0%,
    transparent 55%,
    rgba(5, 5, 5, 0.7) 100%
  );
}
.hero-grid {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: linear-gradient(rgba(204, 0, 0, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(204, 0, 0, 0.05) 1px, transparent 1px);
  background-size: 64px 64px;
  animation: gridDrift 20s linear infinite;
}
@keyframes gridDrift {
  from {
    background-position: 0 0;
  }
  to {
    background-position: 64px 64px;
  }
}

.hero-scan {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    var(--red) 50%,
    transparent 100%
  );
  animation: scan 5s linear infinite;
  opacity: 0.35;
}
@keyframes scan {
  from {
    top: -2px;
  }
  to {
    top: 100%;
  }
}

.hero-content {
  position: relative;
  z-index: 5;
  padding: 0 8%;
  padding-top: 80px;
  max-width: 820px;
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.9s ease, transform 0.9s ease;
}
.hero-content.visible {
  opacity: 1;
  transform: translateY(0);
}

.hero-eyebrow {
  font-family: "Rajdhani", sans-serif;
  font-size: 0.8rem;
  letter-spacing: 6px;
  color: var(--red);
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}
.hero-eyebrow::before {
  content: "";
  width: 36px;
  height: 1px;
  background: var(--red);
}

.hero-title {
  display: flex;
  flex-direction: column;
  font-family: "Black Ops One", cursive;
  line-height: 0.88;
  margin-bottom: 24px;
}
.title-venom {
  font-size: clamp(5rem, 14vw, 11rem);
  color: var(--white);
  text-shadow: 0 0 60px rgba(204, 0, 0, 0.4);
}
.title-gym {
  font-size: clamp(3rem, 8vw, 6.5rem);
  color: var(--red);
  letter-spacing: 8px;
  text-shadow: 0 0 40px rgba(204, 0, 0, 0.6);
}

.hero-sub {
  font-family: "Rajdhani", sans-serif;
  font-size: clamp(1rem, 2.5vw, 1.35rem);
  color: rgba(240, 240, 240, 0.6);
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 36px;
}
.hero-btns {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

/* Hero Stats */
.hero-stats {
  position: absolute;
  bottom: 48px;
  right: 6%;
  z-index: 5;
  display: flex;
  gap: 2px;
}
.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 24px;
  background: rgba(10, 10, 10, 0.7);
  backdrop-filter: blur(12px);
  border-top: 2px solid var(--red);
  min-width: 90px;
  transition: transform 0.3s;
}
.stat:hover {
  transform: translateY(-4px);
}
.stat-num {
  font-family: "Bebas Neue", cursive;
  font-size: 1.8rem;
  color: var(--red);
  line-height: 1;
}
.stat-label {
  font-family: "Rajdhani", sans-serif;
  font-size: 0.7rem;
  color: var(--grey);
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-top: 3px;
}

/* Scroll hint */
.scroll-hint {
  position: absolute;
  bottom: 36px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  z-index: 5;
}
.scroll-hint span {
  width: 1px;
  height: 48px;
  background: linear-gradient(to bottom, var(--red), transparent);
  animation: scrollPulse 1.8s ease-in-out infinite;
}
.scroll-hint p {
  font-family: "Rajdhani", sans-serif;
  font-size: 0.65rem;
  letter-spacing: 3px;
  color: var(--grey);
  text-transform: uppercase;
}
@keyframes scrollPulse {
  0%,
  100% {
    transform: scaleY(1);
    opacity: 0.6;
  }
  50% {
    transform: scaleY(1.4);
    opacity: 1;
  }
}

/* ==============================
   GALLERY
============================== */
.gallery-section {
  background: var(--dark2);
  padding: 80px 0;
  overflow: hidden;
}
.gallery-section .section-header {
  padding: 0 6%;
}

.slider-wrap {
  overflow: hidden;
}
.slide-track {
  display: flex;
  gap: 12px;
  width: max-content;
  animation: galleryScroll 35s linear infinite;
}
.slide-track:hover {
  animation-play-state: paused;
}
.slide {
  position: relative;
  flex-shrink: 0;
  width: 320px;
  height: 210px;
  border-radius: 4px;
  overflow: hidden;
}
.slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}
.slide:hover img {
  transform: scale(1.07);
}
.slide-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(204, 0, 0, 0.35), transparent);
  opacity: 0;
  transition: opacity 0.3s;
}
.slide:hover .slide-overlay {
  opacity: 1;
}
@keyframes galleryScroll {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
}

/* ==============================
   BANNER
============================== */
.banner {
  background: var(--red-dark);
  padding: 0;
}
.banner-inner {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}
.banner-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 32px 28px;
  border-right: 1px solid rgba(255, 255, 255, 0.07);
  opacity: 0;
  transform: translateY(16px);
  transition: all 0.5s ease var(--delay, 0s);
}
.banner-item.revealed {
  opacity: 1;
  transform: translateY(0);
}
.banner-icon {
  font-size: 2rem;
}
.banner-val {
  font-family: "Bebas Neue", cursive;
  font-size: 1.1rem;
  letter-spacing: 1px;
}
.banner-lbl {
  font-family: "Rajdhani", sans-serif;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.65);
  letter-spacing: 1px;
}

/* ==============================
   PRICING
============================== */
.pricing {
  padding: 100px 6%;
  background: var(--black);
}
.plans-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  max-width: 1300px;
  margin: 0 auto;
}
.plan-card {
  background: var(--steel);
  border: 1px solid rgba(255, 255, 255, 0.06);
  padding: 32px 24px;
  position: relative;
  overflow: hidden;
  transition: all 0.35s ease;
  opacity: 0;
  transform: translateY(24px);
}
.plan-card.revealed {
  opacity: 1;
  transform: translateY(0);
}
.plan-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: transparent;
  transition: background 0.3s;
}
.plan-card:hover {
  transform: translateY(-8px);
  border-color: rgba(204, 0, 0, 0.4);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
}
.plan-card:hover::before {
  background: var(--red);
}
.plan-card.featured {
  border-color: rgba(204, 0, 0, 0.5);
  background: linear-gradient(145deg, #1a0a0a, var(--steel));
}
.plan-card.featured::before {
  background: var(--red);
}

.plan-badge {
  position: absolute;
  top: 14px;
  right: -30px;
  background: var(--red);
  color: #fff;
  padding: 5px 40px;
  font-size: 0.7rem;
  font-family: "Rajdhani", sans-serif;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  transform: rotate(40deg);
}
.plan-name {
  font-family: "Bebas Neue", cursive;
  font-size: 1.5rem;
  letter-spacing: 2px;
  color: var(--white);
  margin-bottom: 8px;
}
.plan-amount {
  font-family: "Black Ops One", cursive;
  font-size: 2rem;
  color: var(--red);
}
.plan-top {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.plan-features {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 28px;
}
.plan-features li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.88rem;
  color: rgba(240, 240, 240, 0.7);
  font-family: "Rajdhani", sans-serif;
  font-weight: 600;
}
.plan-features svg {
  width: 14px;
  height: 14px;
  color: var(--red);
  flex-shrink: 0;
}

.plan-btn {
  display: block;
  text-align: center;
  background: transparent;
  border: 1px solid rgba(204, 0, 0, 0.4);
  color: var(--red);
  padding: 11px;
  font-family: "Bebas Neue", cursive;
  font-size: 1rem;
  letter-spacing: 2px;
  text-decoration: none;
  transition: all 0.3s;
}
.plan-btn:hover {
  background: var(--red);
  color: #fff;
  border-color: var(--red);
}
.plan-card.featured .plan-btn {
  background: var(--red);
  color: #fff;
}

/* ==============================
   SERVICES
============================== */
.services {
  padding: 100px 6%;
  background: var(--dark2);
}
.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  max-width: 1300px;
  margin: 0 auto;
}
.service-card {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  border-radius: 2px;
  opacity: 0;
  transform: scale(0.96);
  transition: all 0.5s ease var(--delay, 0s);
}
.service-card.revealed {
  opacity: 1;
  transform: scale(1);
}
.service-img-wrap {
  position: relative;
  height: 280px;
  overflow: hidden;
}
.service-img-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s ease;
  filter: brightness(0.5) saturate(0.7);
}
.service-card:hover .service-img-wrap img {
  transform: scale(1.1);
  filter: brightness(0.3) saturate(0.5);
}
.service-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(140, 0, 0, 0.7) 0%, transparent 60%);
}
.service-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px 18px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}
.service-info h4 {
  font-family: "Bebas Neue", cursive;
  font-size: 1.4rem;
  letter-spacing: 2px;
  color: #fff;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
}
.service-arrow {
  font-size: 1.2rem;
  color: var(--red);
  transition: transform 0.3s;
}
.service-card:hover .service-arrow {
  transform: translateX(4px);
}

/* ==============================
   CTA
============================== */
.cta-section {
  position: relative;
  padding: 120px 6%;
  text-align: center;
  overflow: hidden;
}
.cta-bg {
  position: absolute;
  inset: 0;
  background: url("https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=1600&q=80")
    center/cover;
  filter: brightness(0.12) saturate(0.4);
}
.cta-section::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(120, 0, 0, 0.3) 0%, transparent 60%);
}
.cta-content {
  position: relative;
  z-index: 2;
  max-width: 600px;
  margin: 0 auto;
}
.cta-content h2 {
  font-family: "Black Ops One", cursive;
  font-size: clamp(2.5rem, 6vw, 4.5rem);
  margin-bottom: 16px;
}
.cta-content p {
  color: rgba(240, 240, 240, 0.65);
  font-family: "Rajdhani", sans-serif;
  font-size: 1.1rem;
  letter-spacing: 1px;
  margin-bottom: 36px;
}

/* ==============================
   RESPONSIVE
============================== */
@media (max-width: 900px) {
  .hero-stats {
    position: static;
    flex-wrap: wrap;
    justify-content: center;
    padding: 24px 6%;
    gap: 2px;
  }
  .banner-inner {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 500px) {
  .hero-content {
    padding: 0;
    padding-top: 70px;
  }
  .hero-btns {
    flex-direction: column;
  }
  .banner-inner {
    grid-template-columns: 1fr;
  }
  .services-grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>
